import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export function useResize(
  callback: () => void,
  immediate = false,
  time = 300,
  deps: React.DependencyList = [],
) {
  let timer = useRef<NodeJS.Timeout>().current;
  const handler = () => {
    clearTimeout(timer);
    timer = setTimeout(function () {
      callback();
    }, time);
  };
  useEffect(() => {
    if (immediate) callback();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, deps);
}

export function useScroll(callback: () => void, immediate = false, time = 300) {
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const handler = () => {
    clearTimeout(timer);
    setTimer(
      setTimeout(function () {
        callback();
      }, time),
    );
  };
  useEffect(() => {
    if (immediate) callback();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
}

export function useHeightObserver<T extends HTMLElement>(
  ref: React.RefObject<T>,
  deps: React.DependencyList = [],
) {
  const observer = useRef<ResizeObserver>();
  const [height, setHeight] = useState(0);
  const updateHeight = useCallback(() => {
    setHeight(ref.current?.scrollHeight || 0);
  }, deps);
  useEffect(() => {
    if (!ref?.current) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new ResizeObserver(updateHeight);
    observer.current.observe(ref.current);
    return () => {
      if (ref.current) observer.current?.unobserve(ref.current);
      observer.current?.disconnect();
    };
  }, deps);
  return height;
}
